import type { SimulationNodeData } from '../../models/simulation';

// NodeType includes 'output' for styling purposes, even though it's not a separate node type
type NodeType = SimulationNodeData['type'] | 'output';

/**
 * Gets a CSS variable value from the document root.
 * Falls back to a default value if the variable is not found.
 */
function getCSSVariable(variableName: string, fallback: string): string {
    if (typeof window === 'undefined') {
        return fallback;
    }
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue(variableName)
        .trim();
    return value || fallback;
}

/**
 * Node accent colors - reads from CSS variables to respect theme.
 * Falls back to dark theme values if CSS variables are not available.
 */
export const getNodeAccentColor = (nodeType: NodeType): string => {
    const variableMap: Record<NodeType, string> = {
        distribution: '--color-node-distribution',
        formula: '--color-node-formula',
        output: '--color-node-output'
    };
    const fallbackMap: Record<NodeType, string> = {
        distribution: '#6D5DF6',
        formula: '#4CC2FF',
        output: '#FF8A4C'
    };
    return getCSSVariable(variableMap[nodeType], fallbackMap[nodeType]);
};

/**
 * Node accent soft colors - reads from CSS variables to respect theme.
 * Falls back to dark theme values if CSS variables are not available.
 */
export const getNodeAccentSoftColor = (nodeType: NodeType): string => {
    const variableMap: Record<NodeType, string> = {
        distribution: '--color-node-distribution-soft',
        formula: '--color-node-formula-soft',
        output: '--color-node-output-soft'
    };
    const fallbackMap: Record<NodeType, string> = {
        distribution: 'rgba(109, 93, 246, 0.22)',
        formula: 'rgba(76, 194, 255, 0.22)',
        output: 'rgba(255, 138, 76, 0.22)'
    };
    return getCSSVariable(variableMap[nodeType], fallbackMap[nodeType]);
};

/**
 * Node icon colors - reads from CSS variables to respect theme.
 * Falls back to dark theme values if CSS variables are not available.
 */
export const getNodeIconColor = (nodeType: NodeType): string => {
    const variableMap: Record<NodeType, string> = {
        distribution: '--color-node-distribution-icon',
        formula: '--color-node-formula-icon',
        output: '--color-node-output-icon'
    };
    const fallbackMap: Record<NodeType, string> = {
        distribution: '#9AA2FF',
        formula: '#7BE2FF',
        output: '#FFC499'
    };
    return getCSSVariable(variableMap[nodeType], fallbackMap[nodeType]);
};

export const nodeClassMap: Record<NodeType, string> = {
    distribution: 'distribution-node',
    formula: 'formula-node',
    output: 'output-node'
};


